import { motion } from "framer-motion";

const EASE = [0.83, 0, 0.17, 1];

const chapters = [
  {
    n: "01",
    title: "Relationships first.",
    body: "The strongest opportunities are quiet. They are introduced, not advertised. My work begins with trust, long before contracts, land or capital ever meet the table.",
    image:
      "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
  {
    n: "02",
    title: "Two worlds. One purpose.",
    body: "Backed by over 20 years of experience across the Disability and Community Services Sector, an academic foundation in Psychology and Behaviour Management, and a Licensed Real Estate Agent, I bridge the worlds of People, Property and Possibility to create Strategic Partnerships and Better Housing Outcomes.",
    image:
      "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
  {
    n: "03",
    title: "Housing that works.",
    body: "Every home should perform as an asset, and as a place someone can build a life inside. I help investors, developers and providers deliver both, without compromise.",
    image:
      "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
];

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative bg-plum py-28 md:py-40"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE }}
          className="mb-20 md:mb-28 max-w-3xl"
        >
          <div className="flex items-center gap-3 text-gold text-[11px] tracking-[0.35em] uppercase">
            <span className="w-8 h-px bg-gold" />
            The Manifesto
          </div>
          <h2 className="mt-6 font-serif font-light text-cream text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
            Three principles that shape{" "}
            <span className="italic text-gold">every introduction.</span>
          </h2>
        </motion.div>

        <div className="flex flex-col gap-24 md:gap-40">
          {chapters.map((c, i) => (
            <motion.article
              key={c.n}
              data-testid={`manifesto-chapter-${c.n}`}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.1, ease: EASE }}
              className={`grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center ${
                i % 2 === 1 ? "md:[direction:rtl]" : ""
              }`}
            >
              <div className="md:col-span-6 [direction:ltr]">
                <div className="flex items-baseline gap-6">
                  <span className="font-serif italic text-gold text-7xl md:text-8xl leading-none">
                    {c.n}
                  </span>
                  <span className="h-px flex-1 bg-gold/25" />
                </div>
                <h3 className="mt-8 font-serif font-light text-cream text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                  {c.title}
                </h3>
                <p className="mt-6 text-cream/70 text-base md:text-lg leading-relaxed max-w-lg text-justify hyphens-auto">
                  {c.body}
                </p>
              </div>

              <div className="md:col-span-6 [direction:ltr]">
                <motion.div
                  initial={{ opacity: 0, scale: 1.05 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 1.4, ease: EASE }}
                  className="relative aspect-[4/5] w-full overflow-hidden border border-gold/20"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center image-duotone"
                    style={{ backgroundImage: `url(${c.image})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-plum/10 via-transparent to-plum/70" />
                  <div className="absolute inset-4 border border-gold/15 pointer-events-none" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[10px] tracking-[0.35em] uppercase text-cream/70">
                    <span>Chapter</span>
                    <span>{c.n} / 03</span>
                  </div>
                </motion.div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
